// Must run before the router is created, so ?newUi=… is stripped from the initial URL
import "@/components/newUi/core/applyNewUiFromUrl";
import { createApp } from "vue";

import App from "./App.vue";
import router from "./lib/router";
import axios from "axios";
import store from "./lib/store";
import i18n from "./lib/i18n";
import { VueQueryPlugin, type VueQueryPluginOptions } from "@tanstack/vue-query";
import { queryClient as vueQueryClient } from "@/lib/queryClient";
import { createBootstrap } from "bootstrap-vue-next";
import Clarity from "@microsoft/clarity";
import { CLARITY_ENABLED, TENANT_SLUG } from "@/config";
import { applyAxiosInterceptor } from "@/lib/axios-interceptor";
import vSelect from "vue-select";
import { getAbortController } from "@/lib/abortController";
import "vue-select/dist/vue-select.css";

if (import.meta.env.DEV) {
  axios.defaults.withCredentials = true;
}

axios.defaults.headers.common["X-Tenant-Slug"] = TENANT_SLUG;
axios.defaults.headers.common["X-Fe-Version"] = import.meta.env.VITE_APP_VERSION ?? "unknown";

axios.interceptors.request.use((config) => {
  return {
    ...config,
    signal: getAbortController().signal,
  };
});

applyAxiosInterceptor(axios);

console.info("Clarity is", CLARITY_ENABLED ? "enabled" : "disabled");
if (import.meta.env.VITE_CLARITY_ENABLED === "true") {
  Clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID);
  Clarity.setTag("tenant", TENANT_SLUG);
  Clarity.setTag("env", import.meta.env.MODE);
}

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClient: vueQueryClient,
  enableDevtoolsV6Plugin: true,
};

// Prevent users from closing tab when report is generating
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (store.getters["reports/isLoading"] as boolean) {
    e.preventDefault();
    e.returnValue = ""; // required for most browsers to show the confirmation dialog
  }
};

window.addEventListener("beforeunload", handleBeforeUnload);

const app = createApp(App)
  .use(router)
  .use(store)
  .use(VueQueryPlugin, vueQueryPluginOptions)
  .use(i18n)
  .use(createBootstrap());

// Register components
app.component("VSelect", vSelect);

app.mount("#app");
